import React from 'react'
import { Box, Collapse, IconButton, Stack, TextField, Tooltip } from '@mui/material'
import ClickAwayListener from '@mui/material/ClickAwayListener'
import { alpha, type Theme } from '@mui/material/styles'
import MarkEmailUnreadIcon from '@mui/icons-material/MarkEmailUnreadRounded'
import RadioIcon from '@mui/icons-material/RadioRounded'
import FlagIcon from '@mui/icons-material/FlagRounded'
import SearchIcon from '@mui/icons-material/SearchRounded'
import CloseIcon from '@mui/icons-material/CloseRounded'
import useThreadsStore, { isDefaultFilters, type ThreadFilters } from '../../store/threads'

const iconButtonSx = (active: boolean) => (theme: Theme) => ({
  width: 36,
  height: 36,
  borderRadius: 12,
  padding: 0,
  flexShrink: 0,
  color: active ? theme.palette.primary.main : theme.palette.text.secondary,
  backgroundColor: active
    ? alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.24 : 0.18)
    : 'transparent',
  transition: 'background-color 0.2s ease, color 0.2s ease',
  '&:hover': {
    backgroundColor: active
      ? alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.3 : 0.24)
      : alpha(theme.palette.text.primary, theme.palette.mode === 'dark' ? 0.14 : 0.08),
  },
  '&.Mui-disabled': {
    color: theme.palette.action.disabled,
    backgroundColor: 'transparent',
  },
})

const normalizeQuery = (value: string): string => value.trim()

const ThreadListHeader: React.FC = () => {
  const filters = useThreadsStore((state) => state.filters)
  const defaultPlatforms = useThreadsStore((state) => state.defaultPlatforms)
  const setFilter = useThreadsStore((state) => state.setFilter)
  const resetFilters = useThreadsStore((state) => state.resetFilters)

  const [searchValue, setSearchValue] = React.useState(filters.q ?? '')
  const [searchOpen, setSearchOpen] = React.useState(() => Boolean(filters.q))
  const searchInputRef = React.useRef<HTMLInputElement | null>(null)
  const searchButtonRef = React.useRef<HTMLButtonElement | null>(null)
  const searchFieldId = React.useId()

  React.useEffect(() => {
    setSearchValue(filters.q ?? '')
  }, [filters.q])

  React.useEffect(() => {
    const handle = window.setTimeout(() => {
      const normalized = normalizeQuery(searchValue)
      setFilter('q', normalized)
    }, 300)

    return () => {
      window.clearTimeout(handle)
    }
  }, [searchValue, setFilter])

  React.useEffect(() => {
    if (!searchOpen) {
      return
    }

    const id = window.setTimeout(() => {
      searchInputRef.current?.focus()
      searchInputRef.current?.select()
    }, 0)

    return () => window.clearTimeout(id)
  }, [searchOpen])

  const filtersActive = React.useMemo(
    () => !isDefaultFilters(filters, defaultPlatforms),
    [filters, defaultPlatforms],
  )

  const handleToggle = React.useCallback(
    (key: keyof Pick<ThreadFilters, 'unreadOnly' | 'onAirOnly' | 'flaggedOnly'>) => {
      const next = !filters[key]
      setFilter(key, next)
    },
    [filters, setFilter],
  )

  const handleSearchToggle = () => {
    setSearchOpen((prev) => {
      const next = !prev
      if (!next) {
        window.setTimeout(() => searchButtonRef.current?.focus(), 0)
      }
      return next
    })
  }

  const handleSearchKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key !== 'Escape') {
      return
    }

    event.preventDefault()
    event.stopPropagation()
    setSearchOpen(false)
    window.setTimeout(() => searchButtonRef.current?.focus(), 0)
  }

  const handleReset = () => {
    resetFilters()
    setSearchValue('')
    setSearchOpen(false)
    window.setTimeout(() => searchButtonRef.current?.focus(), 0)
  }

  const handleClickAway = () => {
    if (!searchOpen) {
      return
    }
    setSearchOpen(false)
  }

  const searchActive = searchOpen || Boolean(normalizeQuery(searchValue))

  return (
    <ClickAwayListener onClickAway={handleClickAway} mouseEvent="onMouseDown" touchEvent="onTouchStart">
      <Box
        sx={{
          px: { xs: 1.5, sm: 2 },
          py: 0.5,
          borderBottom: 1,
          borderColor: 'divider',
          position: 'relative',
          minHeight: 40,
          mb: 0.5,
        }}
      >
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          sx={{
            flexWrap: 'nowrap',
            overflowX: 'auto',
            overflowY: 'hidden',
            scrollbarWidth: 'none',
            '&::-webkit-scrollbar': { display: 'none' },
          }}
        >
          <Tooltip title="Toggle unread filter" placement="bottom">
            <span>
              <IconButton
                onClick={() => handleToggle('unreadOnly')}
                aria-label="Toggle Unread filter"
                aria-pressed={filters.unreadOnly}
                sx={iconButtonSx(filters.unreadOnly)}
              >
                <MarkEmailUnreadIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Toggle On-Air filter" placement="bottom">
            <span>
              <IconButton
                onClick={() => handleToggle('onAirOnly')}
                aria-label="Toggle On-Air filter"
                aria-pressed={filters.onAirOnly}
                sx={iconButtonSx(filters.onAirOnly)}
              >
                <RadioIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Toggle Flagged filter" placement="bottom">
            <span>
              <IconButton
                onClick={() => handleToggle('flaggedOnly')}
                aria-label="Toggle Flagged filter"
                aria-pressed={filters.flaggedOnly}
                sx={iconButtonSx(filters.flaggedOnly)}
              >
                <FlagIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Box sx={{ flexGrow: 1 }} />
          <Tooltip title="Search inbox" placement="bottom">
            <span>
              <IconButton
                ref={searchButtonRef}
                onClick={handleSearchToggle}
                aria-label="Search inbox"
                aria-pressed={searchActive}
                aria-expanded={searchOpen ? 'true' : 'false'}
                aria-controls={searchOpen ? searchFieldId : undefined}
                sx={iconButtonSx(searchActive)}
              >
                <SearchIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Reset filters" placement="bottom">
            <span>
              <IconButton
                onClick={handleReset}
                aria-label="Reset filters"
                disabled={!filtersActive}
                sx={iconButtonSx(filtersActive)}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </Stack>
        <Collapse in={searchOpen} timeout="auto" unmountOnExit>
          <Box sx={{ mt: 1.25 }}>
            <TextField
              id={searchFieldId}
              inputRef={searchInputRef}
              size="small"
              fullWidth
              placeholder="Search inbox"
              value={searchValue}
              onChange={(event) => setSearchValue(event.target.value)}
              onKeyDown={handleSearchKeyDown}
              aria-label="Search inbox"
              variant="outlined"
            />
          </Box>
        </Collapse>
      </Box>
    </ClickAwayListener>
  )
}

export default ThreadListHeader
